import React, { useState } from 'react';
import { 
  ClipboardList, 
  Calendar, 
  FileText, 
  Plus,
  ChevronRight
} from 'lucide-react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { juniorSubjects, seniorSubjects, generateTasks } from "../../data/mockAssessments";

export default function AssessmentTasksPage() {
  const [activeYear, setActiveYear] = useState("year10");
  const [newTaskOpen, setNewTaskOpen] = useState(false);

  // Generate data using the shared utility
  const year10Data = juniorSubjects.map(sub => ({ subject: sub, tasks: generateTasks(sub, 10) }));
  const year11Data = seniorSubjects.map(sub => ({ subject: sub, tasks: generateTasks(sub, 11) }));
  const year12Data = seniorSubjects.map(sub => ({ subject: sub, tasks: generateTasks(sub, 12) }));
  const year13Data = seniorSubjects.map(sub => ({ subject: sub, tasks: generateTasks(sub, 13) }));

  const getDataForTab = () => {
    switch(activeYear) {
      case "year10": return year10Data;
      case "year11": return year11Data;
      case "year12": return year12Data;
      case "year13": return year13Data;
      default: return year10Data;
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <ClipboardList className="h-8 w-8 text-primary" />
            Internal Assessments
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and track internal assessment tasks for all year levels.
          </p>
        </div>
        <Button className="gap-2" onClick={() => setNewTaskOpen(true)}>
          <Plus className="h-4 w-4" />
          Create New Task
        </Button>
      </div>

      <Tabs defaultValue="year10" value={activeYear} onValueChange={setActiveYear} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-[600px] mb-6">
          <TabsTrigger value="year10">Year 10</TabsTrigger>
          <TabsTrigger value="year11">Year 11</TabsTrigger>
          <TabsTrigger value="year12">Year 12</TabsTrigger>
          <TabsTrigger value="year13">Year 13</TabsTrigger>
        </TabsList>

        <div className="grid gap-6">
          {getDataForTab().map((item, index) => (
            <Card key={index} className="border-l-4 border-l-primary/50">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value={`item-${index}`} className="border-0">
                  <AccordionTrigger className="px-6 hover:no-underline py-4">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                          {item.subject.charAt(0)}
                        </div>
                        <div className="text-left">
                          <h3 className="font-bold text-lg">{item.subject}</h3>
                          <p className="text-sm text-muted-foreground font-normal">
                            {item.tasks.length} Assessment Tasks Planned
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 text-xs">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {item.tasks.filter(t => t.status === "Completed").length} Done
                        </Badge>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {item.tasks.filter(t => t.status === "Active").length} Active
                        </Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-0">
                    <div className="rounded-md border mt-4">
                      <div className="grid grid-cols-12 gap-4 p-4 bg-muted/50 font-medium text-sm text-muted-foreground border-b">
                        <div className="col-span-5">Task Title</div>
                        <div className="col-span-2">Type</div>
                        <div className="col-span-2">Weighting</div>
                        <div className="col-span-2">Due Date</div>
                        <div className="col-span-1 text-right">Status</div>
                      </div>
                      {item.tasks.map((task) => (
                        <div key={task.id} className="grid grid-cols-12 gap-4 p-4 border-b last:border-0 items-center hover:bg-muted/20 transition-colors">
                          <div className="col-span-5 font-medium flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            {task.title}
                          </div>
                          <div className="col-span-2 text-sm">{task.type}</div>
                          <div className="col-span-2 text-sm font-mono bg-secondary/20 px-2 py-1 rounded w-fit">
                            {task.weighting}%
                          </div>
                          <div className="col-span-2 text-sm flex items-center gap-1 text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {task.dueDate}
                          </div>
                          <div className="col-span-1 text-right">
                            <Badge variant={
                              task.status === "Completed" ? "default" : 
                              task.status === "Active" ? "secondary" : "outline"
                            } className={
                              task.status === "Completed" ? "bg-green-500 hover:bg-green-600" :
                              task.status === "Active" ? "bg-blue-500 text-white hover:bg-blue-600" : ""
                            }>
                              {task.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button variant="outline" size="sm" className="gap-2">
                        <Plus className="h-3 w-3" /> Add Assessment Task
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          ))}
        </div>
      </Tabs>

      {/* Create Task Modal */}
      <Dialog open={newTaskOpen} onOpenChange={setNewTaskOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Assessment Task</DialogTitle>
            <DialogDescription>
              Define a new internal assessment for a specific subject and year level.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Year Level</Label>
                <Select defaultValue={activeYear}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="year10">Year 10</SelectItem>
                    <SelectItem value="year11">Year 11</SelectItem>
                    <SelectItem value="year12">Year 12</SelectItem>
                    <SelectItem value="year13">Year 13</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subject</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {seniorSubjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Task Title</Label>
              <Input placeholder="e.g. Research Project: Climate Change" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Weighting (%)</Label>
                <Input type="number" placeholder="20" />
              </div>
              <div className="space-y-2">
                <Label>Due Week</Label>
                <Input placeholder="Term 1, Week 8" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTaskOpen(false)}>Cancel</Button>
            <Button onClick={() => setNewTaskOpen(false)}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
